import { toast } from 'sonner'

import {
    AccountType,
    ContactDetails,
    CreateUserPayload,
    FeaturePromotion,
    FeedbackId,
    Name,
    UpdateAddressesPayload,
    UsagePolicyKey,
    User,
} from '@/common/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
    updateContactDetails,
    updateName,
    updateAddresses,
    updateTfn,
    fetchUser,
    sendMobileOtp,
    verifyMobileOtp,
    updateMobile,
    updateSurveyData,
    getUsagePolicy,
    acceptPolicy,
    contactSupport,
    trackResetPassword,
    createUser,
    updateAccountType,
    acceptTerms,
    updateTodos,
    fetchUserRewards,
    viewFeaturePromotion,
    submitFeedback,
} from '@/user/userApi'
import { Errors } from '@/common/strings'
import { API_ROUTES } from '@/common/constants'
import { SurveyT } from '@/user/survey/SurveyPage'
import { AxiosError } from 'axios'
import { NetworkError } from '@/common/api/error'
import useAccountStore from '@/account/accountStore'

export const useUpdateContactDetails = () => {
    return useMutation({
        mutationFn: (data: ContactDetails) => updateContactDetails(data),
        onSuccess: () => {
            toast.success('Updated the contact details successfully')
        },
        onError: (err) => {
            if (err instanceof NetworkError) return
            toast.error(Errors.server)
        },
    })
}

export const useCreateUser = () => {
    return useMutation({
        mutationFn: (data: CreateUserPayload) => createUser(data),
        onError: (err) => {
            console.log('Failed to create user', err)
            toast.error(Errors.server)
        },
    })
}

export const useUpdateName = () => {
    return useMutation({
        mutationFn: (data: Name) => updateName(data),
        onError: (err) => {
            if (err instanceof NetworkError) return
            console.log('Failed to update contact details', err)
            toast.error(Errors.server)
        },
    })
}

export const useUpdateMobile = () => {
    return useMutation({
        mutationFn: (data?: string) => updateMobile(data),
        onError: (err: AxiosError) => {
            if (err instanceof NetworkError) return
            console.log('Failed to update mobile and issue otp', err)
            // @ts-ignore
            const errorMessage =
                err.response?.status == 409
                    ? 'Sorry, that phone number has already been registered. Please use a different number or contact us if you need help.'
                    : Errors.server
            toast.error(errorMessage)
        },
    })
}

export const useUpdateTodos = () => {
    return useMutation({
        mutationFn: (todoId: string) => updateTodos(todoId),
    })
}

export const useFeaturePromotionViewed = () => {
    return useMutation({
        mutationFn: (featurePromotion: FeaturePromotion) => viewFeaturePromotion(featurePromotion),
    })
}

export const useSubmitFeedback = () => {
    return useMutation({
        mutationFn: ({
            feedbackId,
            data,
        }: {
            feedbackId: FeedbackId
            data?: { questions: { question: string; response: string | string[] }[]; referenceId: string }
        }) => submitFeedback(feedbackId, data),
    })
}

type ResendOTPResponse = {
    message: string
    waitTime: number
    sendCodeAttempts: number
}

export const useSendMobileOtp = () => {
    return useMutation<ResendOTPResponse>({
        mutationFn: async () => {
            try {
                const res = await sendMobileOtp()
                return res
            } catch (err: any) {
                // If API returns waitTime, surface it
                if (err?.response?.data?.waitTime) {
                    return Promise.reject({ waitTime: err.response.data.waitTime, message: err.response.data.message })
                }
                throw err
            }
        },
        onError: (err: any) => {
            if (err instanceof NetworkError) return
            if (err?.waitTime) {
                toast.error(`${err.message}. Please wait ${err.waitTime} seconds.`)
            } else {
                toast.error(Errors.server)
            }
        },
    })
}

export const useVerifyMobileOtp = () => {
    return useMutation({
        mutationFn: (data: string) => verifyMobileOtp(data),
        onError: (err) => {
            if (err instanceof NetworkError) return
            toast.error(Errors.server)
        },
    })
}

export const useUpdateSurveyData = () => {
    return useMutation({
        mutationFn: (data: SurveyT) => updateSurveyData(data),
        onError: (err) => {
            if (err instanceof NetworkError) return
            console.log('Failed to update survey data', err)
            toast.error(Errors.server)
        },
    })
}

export const useUpdateUserAccountType = () => {
    return useMutation({
        mutationFn: (accountType: AccountType) => updateAccountType(accountType),
        onError: (err) => {
            if (err instanceof NetworkError) return
            console.log('Failed to update account type data', err)
            toast.error(Errors.server)
        },
    })
}

export const useUpdateAddresses = () => {
    return useMutation({
        mutationFn: (data: UpdateAddressesPayload) => updateAddresses(data),
        onError: (err) => {
            if (err instanceof NetworkError) return
            console.log('Failed to update addresses', err)
            toast.error(Errors.server)
        },
    })
}

export const useUpdateTfn = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (tfn: string) => updateTfn({ tfn }),
        onSuccess: () => {
            queryClient.setQueryData([API_ROUTES.user], (old: User | undefined) =>
                old ? { ...old, tfnProvided: true } : old,
            )
        },
        onError: (err) => {
            if (err instanceof NetworkError) return
            console.log('Failed to update TFN', err)
            toast.error(Errors.server)
        },
    })
}

export const useFetchUser = (isEnabled = true) => {
    return useQuery({
        queryKey: [API_ROUTES.user],
        queryFn: fetchUser,
        refetchOnMount: true,
        enabled: isEnabled,
        gcTime: 0,
        staleTime: 0,
    })
}

export const useFetchRewards = (isEnabled = true) => {
    return useQuery({
        queryKey: [API_ROUTES.userRewards],
        queryFn: () => fetchUserRewards().then((res) => res.items),
        refetchOnMount: true,
        enabled: isEnabled,
    })
}

export const useAcceptPolicy = (
    onError = (err: any) => {
        if (err instanceof NetworkError) return
        console.log('Failed to update user consent', err)
        toast.error(Errors.server)
    },
) => {
    return useMutation({
        mutationFn: (versionId: string) => acceptPolicy(versionId),
        onError,
    })
}

export const useAcceptTerms = (
    onError = (err: any) => {
        if (err instanceof NetworkError) return
        console.log('Failed to update user consent', err)
        toast.error(Errors.server)
    },
) => {
    return useMutation({
        mutationFn: (versionIds: string[]) => acceptTerms(versionIds),
        onSuccess: async () => {
            // Optimistically update the active account with termsAccepted
            const activeAccountId = useAccountStore.getState().activeAccountId
            if (!activeAccountId) return

            useAccountStore.getState().confirmTermsAccepted(activeAccountId)
        },
        onError,
    })
}

export const useFetchUsagePolicy = (policyKey: UsagePolicyKey, options?: { isEnabled?: boolean }) => {
    return useQuery({
        queryKey: [API_ROUTES.usagePolicies, policyKey],
        queryFn: () => getUsagePolicy(policyKey),
        refetchOnMount: true,
        enabled: options?.isEnabled ?? true,
    })
}

export const useContactSupport = () => {
    return useMutation({
        mutationFn: (args: { message: string }) => contactSupport(args),
        onError: (err) => {
            if (err instanceof NetworkError) return
            console.log('Failed to contact support', err)
            toast.error(Errors.server)
        },
    })
}

export const useTrackPasswordReset = () => {
    return useMutation({
        mutationFn: (args: { emailId: string }) => trackResetPassword(args),
        onError: (err) => {
            console.log('Failed to track reset password', err)
        },
    })
}
